import React, { useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import LoadingIndicator from '../../components/LoadingIndicator';
import Timestamp from '../../components/Timestamp';
import Tooltip from '../../components/Tooltip';
import CommentContext from '../../utils/post/CommentContext';
import FoldedComments from './FoldedComments';
import HistoricalComment from './HistoricalComment';
import styles from './HistoricalCommentThread.module.scss';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import ReactionAvatarStack from './ReactionAvatarStack';

const THREAD_FRAGMENT = gql(`
  fragment HistoricalCommentThread_thread on Thread {
    id
    resolver {
      id
      name
    }
    comments {
      id
      ...HistoricalComment_comment
    }
    reactions {
      id
      ...ReactionAvatarStack_reaction
    }
    resolvedAt
    context
  }
`);

interface HistoricalCommentThreadProps {
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  onUnresolve: () => Promise<void>;
}

const HistoricalCommentThread = ({
  thread,
  onUnresolve,
}: HistoricalCommentThreadProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [resolving, setResolving] = useState(false);
  const resolved = thread.resolver && thread.resolvedAt;

  return (
    <>
      <div className={styles.resolverContainer}>
        <div className={styles.resolvedText}>
          {resolved ? (
            <>
              {currentUser.id === thread.resolver?.id
                ? 'You'
                : thread.resolver?.name}{' '}
              resolved <Timestamp date={thread.resolvedAt} />
            </>
          ) : (
            'Text deleted'
          )}
        </div>
        {resolved ? (
          <Button
            variant="link"
            size="small"
            disabled={resolving}
            onClick={() => {
              setResolving(true);
              onUnresolve().catch(() => setResolving(false));
            }}
          >
            {resolving ? <LoadingIndicator delay={0} /> : 'Unresolve'}
          </Button>
        ) : (
          <Tooltip
            trigger="clickOrHover"
            content={
              'Cannot unresolve comment\nbecause the commented text\nhas been deleted from the post'
            }
          >
            <div className={styles.outdatedText}>Outdated</div>
          </Tooltip>
        )}
      </div>
      <div id={`thread-${thread.id}`} className={styles.thread}>
        <div className={styles.context}>
          <CommentContext context={thread.context} />
        </div>
        {thread.reactions && thread.reactions.length > 0 && (
          <div className={styles.reactionListContainer}>
            <ReactionAvatarStack reactions={thread.reactions} />
          </div>
        )}
        <FoldedComments
          key={thread.id}
          comments={thread.comments}
          renderComment={(comment) => (
            <HistoricalComment comment={comment} key={comment.id} />
          )}
        />
      </div>
    </>
  );
};

export default HistoricalCommentThread;
